// packages/ui/src/forms/fields/FormTextField.tsx
'use client';

import { Box, InputAdornment, TextField, type TextFieldProps } from '@mui/material';
import type { ComponentType, ReactElement, ReactNode } from 'react';
import { Controller, useFormContext, type FieldValues, type Path } from 'react-hook-form';

import { HelperTextWithLength } from '../../components/HelperTextWithLength';
import {
  getFieldFillSx,
  outlinedInputSize,
  sharedFieldRowSx,
  sharedLeftIconBoxSx,
  sharedLeftIconOnInputSx,
} from './_styles';
import { useFormT } from '../_i18n/FormI18nContext';
import { renderFieldError } from '../_i18n/renderFieldError';

export type FormTextFieldProps<TFieldValues extends FieldValues> = Omit<TextFieldProps, 'name' | 'defaultValue'> & {
  name: Path<TFieldValues>;
  leftIcon?: ReactNode;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  highlightWhenEmpty?: boolean;
  /** See `getFieldFillSx`. Defaults to `'white'`. */
  background?: 'white' | 'transparent';
  /**
   * Caps the input length AND renders a live `current/max` counter that turns
   * red the instant the value exceeds the limit. Unlike a bare `htmlInput`
   * `maxLength` (which only blocks keystrokes/paste), this also surfaces
   * over-limit values set programmatically — AI generation, in-app translate,
   * or browser Google Translate mutating the field.
   */
  maxLength?: number;
  /**
   * Fields whose validation depends on this one (react-hook-form's `deps`): once
   * a submit was attempted, a change of this field re-validates them together
   * with it. For a schema rule that spans several fields and marks them all —
   * a name required in at least ONE language marks every language's name — so
   * that satisfying the rule in one field clears the error on the others, which
   * react-hook-form otherwise leaves stale (it re-validates only the field that
   * changed).
   */
  deps?: ReadonlyArray<Path<TFieldValues>>;
  /**
   * The value the field takes when it loses focus, when that differs from what it holds: the text the
   * way it will be STORED, shown before the save instead of after the next reload (a description's
   * Markdown emphasis as HTML — `FormEmphasisTextField`). The change is a change of the field like any
   * typed one (dirty, re-validated, counted against `maxLength`).
   */
  normalizeOnBlur?: (value: string) => string;
};

/**
 * The text field a form field draws: MUI's TextField, or the chat widget's outlined-only one
 * (`components/chat/outlinedFormFields.tsx`, docs/embed-size-and-speed.md §4).
 */
export type TextFieldComponent = ComponentType<TextFieldProps>;

export function FormTextField<TFieldValues extends FieldValues>(props: FormTextFieldProps<TFieldValues>) {
  return useFormTextField(TextField, props);
}

/**
 * FormTextField, drawn with `TextFieldComponent`. The chat widget's pre-chat form draws its fields with
 * OutlinedTextField through this, so chat.js carries neither MUI's TextField nor its Select; every other form uses
 * `FormTextField`.
 */
export function useFormTextField<TFieldValues extends FieldValues>(
  TextFieldComponent: TextFieldComponent,
  {
    name,
    helperText,
    leftIcon,
    startIcon,
    endIcon,
    highlightWhenEmpty = false,
    background = 'white',
    maxLength,
    deps,
    normalizeOnBlur,
    slotProps,
    onChange: onChangeProp,
    ...props
  }: FormTextFieldProps<TFieldValues>,
): ReactElement {
  const { control } = useFormContext<TFieldValues>();
  const t = useFormT();

  return (
    <Controller
      name={name}
      control={control}
      rules={deps === undefined ? undefined : { deps: [...deps] }}
      render={({ field, fieldState }) => {
        const errorText = renderFieldError(fieldState.error?.message, t);
        // `field.value` is RHF's `any`; route through `unknown` so the typeof
        // guard narrows safely (no-unsafe-assignment) without an `as` cast.
        const fieldValue: unknown = field.value;
        const charLen = typeof fieldValue === 'string' ? fieldValue.length : 0;
        const overLimit = maxLength != null && charLen > maxLength;
        const baseHelper = errorText ?? helperText;
        const helperNode =
          maxLength != null ? (
            <HelperTextWithLength helper={baseHelper} length={charLen} max={maxLength} />
          ) : (
            baseHelper
          );
        // Route react-hook-form's field ref to the underlying <input> via
        // `inputRef`. Spreading `{...field}` puts the ref on TextField's
        // `ref`, which targets the wrapper <div> — making `form.setFocus`,
        // error-scroll, and other ref-based RHF features silently no-op.
        const { ref: fieldRef, ...fieldRest } = field;

        return (
          <Box sx={sharedFieldRowSx()}>
            {leftIcon ? (
              <Box
                sx={{
                  ...sharedLeftIconBoxSx(),
                  ...(helperNode || props.multiline ? sharedLeftIconOnInputSx(outlinedInputSize(props.size)) : {}),
                }}
              >
                {leftIcon}
              </Box>
            ) : null}

            <TextFieldComponent
              {...props}
              {...fieldRest}
              inputRef={fieldRef}
              onChange={(e) => { field.onChange(e); onChangeProp?.(e); }}
              onBlur={() => {
                if (normalizeOnBlur !== undefined && typeof fieldValue === 'string') {
                  const normalized = normalizeOnBlur(fieldValue);
                  if (normalized !== fieldValue) field.onChange(normalized);
                }
                field.onBlur();
              }}
              value={field.value ?? ''}
              error={!!fieldState.error || overLimit}
              helperText={helperNode}
              sx={{ flex: 1, ...getFieldFillSx(highlightWhenEmpty && !(field.value as string | undefined)?.trim(), background), ...(props.sx as object) }}
              slotProps={{
                ...slotProps,
                htmlInput:
                  maxLength != null
                    ? { ...(slotProps?.htmlInput as object), maxLength }
                    : slotProps?.htmlInput,
                input: (ownerState) => {
                  const baseInputProps =
                    typeof slotProps?.input === 'function'
                      ? slotProps.input(ownerState)
                      : (slotProps?.input ?? {});

                  return {
                    ...baseInputProps,
                    startAdornment: startIcon ? (
                      <InputAdornment position="start">{startIcon}</InputAdornment>
                    ) : baseInputProps.startAdornment,
                    endAdornment: endIcon ? (
                      <InputAdornment position="end">{endIcon}</InputAdornment>
                    ) : baseInputProps.endAdornment,
                  };
                },
              }}
            />
          </Box>
        );
      }}
    />
  );
}
